import React, { useMemo } from 'react';
import type { BonusPlan, BonusPlanMode, PayrollPosition } from '../../types/finances';
import { BONUS_PLAN_MODE_LABELS, BONUS_PLAN_MODES } from '../../types/finances';
import { createBlankBonusSlice, type ComputedBonusPlan } from '../../utils/bonusPlan';
import { formatScenarioMoney } from '../../utils/cashFlowScenario';
import ProfitShareBar from './ProfitShareBar';

interface BonusPlannerModalProps {
  year: number;
  years: number[];
  onYearChange: (year: number) => void;
  plan: BonusPlan;
  onChange: (plan: BonusPlan) => void;
  computed: ComputedBonusPlan;
  positions: PayrollPosition[];
  estimatedProfit: number;
  onProfitChange: (profit: number) => void;
  profitHint?: number | null;
  baselineLowest: number | null;
  draftLowest: number | null;
  busy?: boolean;
  error?: string | null;
  onSave: () => void;
}

function toInputDate(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function fromInputDate(s: string, fallback: Date): Date {
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return fallback;
  return new Date(y, m - 1, d);
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const BonusPlannerModal: React.FC<BonusPlannerModalProps> = ({
  year,
  years,
  onYearChange,
  plan,
  onChange,
  computed,
  positions,
  estimatedProfit,
  onProfitChange,
  profitHint = null,
  baselineLowest,
  draftLowest,
  busy = false,
  error = null,
  onSave,
}) => {
  const patch = (next: Partial<BonusPlan>) => onChange({ ...plan, ...next });
  const lowestDelta =
    baselineLowest != null && draftLowest != null ? draftLowest - baselineLowest : null;

  const byId = useMemo(
    () => Object.fromEntries(computed.people.map((p) => [p.positionId, p])),
    [computed.people],
  );

  return (
    <div className="fin-obl-embed">
      <p className="fin-modal-help">
        Each included person gets their own % of year-end profit. Paid on a date — not mixed into monthly burn.
      </p>
      <div className="fin-obl-body fin-obl-body--embedded">
          <div className="fin-obl-toolbar">
            <label className="fin-modal-field">
              <span className="fin-modal-label">Year</span>
              <select
                className="fin-modal-select"
                value={year}
                onChange={(e) => onYearChange(Number(e.target.value))}
              >
                {years.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </label>
            <label className="fin-modal-toggle">
              <input
                type="checkbox"
                checked={plan.enabled}
                onChange={(e) => patch({ enabled: e.target.checked })}
              />
              Include on live cash line
            </label>
            <label className="fin-modal-toggle">
              <input
                type="checkbox"
                checked={plan.includeEmployerTax}
                onChange={(e) => patch({ includeEmployerTax: e.target.checked })}
              />
              Add employer tax
            </label>
          </div>

          <div className="fin-obl-kpis">
            <div className="fin-kpi-card">
              <span className="fin-kpi-label">Year-end profit</span>
              <span className="fin-kpi-value">{formatScenarioMoney(estimatedProfit)}</span>
              <span className="fin-kpi-sub">Same profit number as the tax planner</span>
            </div>
            <div className="fin-kpi-card">
              <span className="fin-kpi-label">Bonus pool</span>
              <span className="fin-kpi-value">{formatScenarioMoney(computed.bonusPool || computed.grossTotal)}</span>
              <span className="fin-kpi-sub">
                {plan.mode === 'percent_of_profit'
                  ? `${computed.eligibleCount} people · each has their own %`
                  : `${computed.eligibleCount} eligible people`}
              </span>
            </div>
            <div className="fin-kpi-card">
              <span className="fin-kpi-label">Employer tax</span>
              <span className="fin-kpi-value">{formatScenarioMoney(computed.employerTaxTotal)}</span>
              <span className="fin-kpi-sub">FICA / FUTA / SUTA on the bonus</span>
            </div>
            <div className="fin-kpi-card">
              <span className="fin-kpi-label">Left after bonuses</span>
              <span className="fin-kpi-value">{formatScenarioMoney(computed.profitAfterBonuses)}</span>
              <span className="fin-kpi-sub">
                {lowestDelta != null
                  ? `Lowest cash ${lowestDelta < 0 ? '' : '+'}${formatScenarioMoney(lowestDelta)} vs saved`
                  : 'Profit minus pool and employer tax'}
              </span>
            </div>
          </div>

          <ProfitShareBar
            profit={estimatedProfit}
            bonusPool={computed.bonusPool || computed.grossTotal}
            employerTax={computed.employerTaxTotal}
          />

          <div className="fin-obl-grid">
            <label className="fin-modal-field">
              <span className="fin-modal-label">How to calculate</span>
              <select
                className="fin-modal-select"
                value={plan.mode}
                onChange={(e) => patch({ mode: e.target.value as BonusPlanMode })}
              >
                {BONUS_PLAN_MODES.map((m) => (
                  <option key={m} value={m}>{BONUS_PLAN_MODE_LABELS[m]}</option>
                ))}
              </select>
            </label>
            {(plan.mode === 'percent_of_profit' || plan.mode === 'percent_of_salary') && (
              <label className="fin-modal-field">
                <span className="fin-modal-label">
                  {plan.mode === 'percent_of_profit' ? 'Default % of profit if blank' : 'Default % of salary'}
                </span>
                <input
                  className="fin-modal-input"
                  type="number"
                  min={0}
                  step={0.5}
                  value={plan.defaultPercent}
                  onChange={(e) => patch({ defaultPercent: Number(e.target.value) || 0 })}
                />
              </label>
            )}
            {plan.mode === 'percent_of_profit' && (
              <label className="fin-modal-field">
                <span className="fin-modal-label">Year-end profit</span>
                <input
                  className="fin-modal-input"
                  type="number"
                  min={0}
                  step={1000}
                  value={estimatedProfit || ''}
                  onChange={(e) => onProfitChange(Number(e.target.value) || 0)}
                />
                {profitHint != null && (
                  <button
                    type="button"
                    className="fin-obl-hint"
                    onClick={() => onProfitChange(profitHint)}
                  >
                    Use last 12 mo QB net ({formatScenarioMoney(profitHint)})
                  </button>
                )}
              </label>
            )}
            {plan.mode === 'fixed' && (
              <label className="fin-modal-field">
                <span className="fin-modal-label">Default $ per person</span>
                <input
                  className="fin-modal-input"
                  type="number"
                  min={0}
                  step={100}
                  value={plan.defaultFixedAmount}
                  onChange={(e) => patch({ defaultFixedAmount: Number(e.target.value) || 0 })}
                />
              </label>
            )}
            {plan.mode === 'profit_pool' && (
              <label className="fin-modal-field">
                <span className="fin-modal-label">Pool amount</span>
                <input
                  className="fin-modal-input"
                  type="number"
                  min={0}
                  step={1000}
                  value={plan.poolAmount}
                  onChange={(e) => patch({ poolAmount: Number(e.target.value) || 0 })}
                />
              </label>
            )}
            <label className="fin-modal-field">
              <span className="fin-modal-label">Payout</span>
              <select
                className="fin-modal-select"
                value={plan.payoutSplit}
                onChange={(e) => patch({ payoutSplit: e.target.value === 'custom' ? 'custom' : 'lump' })}
              >
                <option value="lump">One date</option>
                <option value="custom">Split across dates</option>
              </select>
            </label>
            {plan.payoutSplit === 'lump' && (
              <>
                <label className="fin-modal-field">
                  <span className="fin-modal-label">Month</span>
                  <select
                    className="fin-modal-select"
                    value={plan.payoutMonth}
                    onChange={(e) => patch({ payoutMonth: Number(e.target.value) })}
                  >
                    {MONTHS.map((label, i) => (
                      <option key={label} value={i + 1}>{label}</option>
                    ))}
                  </select>
                </label>
                <label className="fin-modal-field">
                  <span className="fin-modal-label">Day</span>
                  <input
                    className="fin-modal-input"
                    type="number"
                    min={1}
                    max={31}
                    value={plan.payoutDay}
                    onChange={(e) => patch({ payoutDay: Number(e.target.value) || 1 })}
                  />
                </label>
              </>
            )}
          </div>

          {plan.payoutSplit === 'custom' && (
            <div className="fin-obl-slices">
              {plan.customSlices.map((slice, idx) => (
                <div key={slice.id} className="fin-obl-slice-row">
                  <input
                    className="fin-modal-input"
                    value={slice.label ?? ''}
                    placeholder={`Payment ${idx + 1}`}
                    onChange={(e) => {
                      const customSlices = plan.customSlices.map((s) =>
                        s.id === slice.id ? { ...s, label: e.target.value } : s,
                      );
                      patch({ customSlices });
                    }}
                  />
                  <input
                    className="fin-modal-input"
                    type="date"
                    value={toInputDate(slice.date)}
                    onChange={(e) => {
                      const customSlices = plan.customSlices.map((s) =>
                        s.id === slice.id ? { ...s, date: fromInputDate(e.target.value, s.date) } : s,
                      );
                      patch({ customSlices });
                    }}
                  />
                  <input
                    className="fin-modal-input"
                    type="number"
                    min={0}
                    value={slice.percent}
                    onChange={(e) => {
                      const customSlices = plan.customSlices.map((s) =>
                        s.id === slice.id ? { ...s, percent: Number(e.target.value) || 0 } : s,
                      );
                      patch({ customSlices });
                    }}
                  />
                  <button
                    type="button"
                    className="fin-secondary-btn"
                    onClick={() => patch({ customSlices: plan.customSlices.filter((s) => s.id !== slice.id) })}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="fin-secondary-btn"
                onClick={() => patch({ customSlices: [...plan.customSlices, createBlankBonusSlice(year)] })}
              >
                Add payout date
              </button>
            </div>
          )}

          <div className="fin-forecast-table-scroll">
            <table className="fin-forecast-table" aria-label="Bonus by position">
              <thead>
                <tr>
                  <th>Include</th>
                  <th>Position</th>
                  <th className="right">Salary</th>
                  {(plan.mode === 'percent_of_salary' || plan.mode === 'percent_of_profit') && (
                    <th className="right">{plan.mode === 'percent_of_profit' ? '% of profit' : 'Override %'}</th>
                  )}
                  <th className="right">Override $</th>
                  <th className="right">Gross</th>
                  <th className="right">Tax</th>
                  <th className="right">Cash out</th>
                </tr>
              </thead>
              <tbody>
                {positions.map((p) => {
                  const row = byId[p.id];
                  const ov = plan.overrides.find((o) => o.positionId === p.id);
                  const included = ov ? ov.included : row?.included ?? false;
                  return (
                    <tr key={p.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={included}
                          onChange={(e) => {
                            const next = plan.overrides.filter((o) => o.positionId !== p.id);
                            next.push({
                              positionId: p.id,
                              included: e.target.checked,
                              percent: ov?.percent ?? null,
                              amount: ov?.amount ?? null,
                            });
                            patch({ overrides: next });
                          }}
                        />
                      </td>
                      <td>{p.title}</td>
                      <td className="right">{formatScenarioMoney(row?.annualSalary ?? p.annualSalary)}</td>
                      {(plan.mode === 'percent_of_salary' || plan.mode === 'percent_of_profit') && (
                        <td className="right">
                          <input
                            className="fin-modal-input fin-obl-inline-input"
                            type="number"
                            min={0}
                            step={0.1}
                            placeholder={plan.mode === 'percent_of_profit' ? String(plan.defaultPercent || 0) : '—'}
                            value={ov?.percent ?? ''}
                            onChange={(e) => {
                              const next = plan.overrides.filter((o) => o.positionId !== p.id);
                              next.push({
                                positionId: p.id,
                                included,
                                percent: e.target.value === '' ? null : Number(e.target.value),
                                amount: ov?.amount ?? null,
                              });
                              patch({ overrides: next });
                            }}
                          />
                        </td>
                      )}
                      <td className="right">
                        <input
                          className="fin-modal-input fin-obl-inline-input"
                          type="number"
                          min={0}
                          placeholder="—"
                          value={ov?.amount ?? ''}
                          onChange={(e) => {
                            const next = plan.overrides.filter((o) => o.positionId !== p.id);
                            next.push({
                              positionId: p.id,
                              included,
                              percent: ov?.percent ?? null,
                              amount: e.target.value === '' ? null : Number(e.target.value),
                            });
                            patch({ overrides: next });
                          }}
                        />
                      </td>
                      <td className="right">{formatScenarioMoney(row?.grossBonus ?? 0)}</td>
                      <td className="right">{formatScenarioMoney(row?.employerTax ?? 0)}</td>
                      <td className="right">{formatScenarioMoney(row?.cashOut ?? 0)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
      </div>

      <div className="fin-cockpit-panel-actions">
        {error && <span className="fin-modal-error">{error}</span>}
        <button type="button" className="fin-primary-btn" onClick={onSave} disabled={busy}>
          Save bonus plan
        </button>
      </div>
    </div>
  );
};

export default BonusPlannerModal;
